// CPC LOCAL homepage: Push Hub CLOB stats + fullnode RPC fallbacks (no suix_* indexer).

import { useSuiClientQuery } from '@mysten/dapp-kit';
import { useQuery } from '@tanstack/react-query';

import {
	fetchPushHubHealth,
	fetchPushHubKline,
	type PushHubHealth,
	type PushHubKlineCandle,
} from '~/hooks/pushHubClient';

export type CpcKlineVolumePoint = {
	openTimeMs: number;
	volumeLots: number;
};

const HOME_REFETCH_MS = 30_000;

function klineToVolumeSeries(klines: PushHubKlineCandle[]): CpcKlineVolumePoint[] {
	return klines
		.map((c) => ({
			openTimeMs: c.open_time_ms ?? 0,
			volumeLots: Number(c.volume_lots ?? 0),
		}))
		.filter((p) => p.openTimeMs > 0)
		.sort((a, b) => a.openTimeMs - b.openTimeMs);
}

export function useCpcHomeStats() {
	const pushHub = useQuery({
		queryKey: ['cpc-push-hub', 'home-stats'],
		queryFn: fetchPushHubHealth,
		refetchInterval: HOME_REFETCH_MS,
		retry: 2,
		staleTime: 15_000,
	});

	const kline = useQuery({
		queryKey: ['cpc-push-hub', 'home-kline', '1h'],
		queryFn: () => fetchPushHubKline('1h', 48),
		refetchInterval: HOME_REFETCH_MS,
		retry: 2,
		staleTime: 15_000,
		enabled: pushHub.isSuccess,
	});

	const { data: totalTxBlocks } = useSuiClientQuery(
		'getTotalTransactionBlocks',
		{},
		{ staleTime: 60_000, retry: 3 },
	);

	const { data: latestCheckpointSeq } = useSuiClientQuery(
		'getLatestCheckpointSequenceNumber',
		{},
		{ staleTime: 15_000, retry: 3 },
	);

	const checkpoint = useSuiClientQuery(
		'getCheckpoint',
		{ id: latestCheckpointSeq != null ? String(latestCheckpointSeq) : '' },
		{ enabled: latestCheckpointSeq != null && latestCheckpointSeq !== '', staleTime: 15_000 },
	);

	const volumeSeries = klineToVolumeSeries(kline.data ?? []);
	const lastPoint = volumeSeries.length > 0 ? volumeSeries[volumeSeries.length - 1] : undefined;
	const lastHourVolume = lastPoint?.volumeLots;

	const hub = pushHub.data;
	const trade = hub?.trade_stats;
	const orders = hub?.order_stats;

	return {
		isLoading: pushHub.isPending,
		isError: pushHub.isError,
		hub: hub as PushHubHealth | undefined,
		totalTxBlocks,
		checkpointSeq:
			hub?.latest_checkpoint ??
			(latestCheckpointSeq != null ? Number(latestCheckpointSeq) : undefined),
		epoch: checkpoint.data?.epoch != null ? Number(checkpoint.data.epoch) : undefined,
		chainEvents: hub?.chain_events ?? hub?.hub_events,
		tradesTotal: trade?.total_count,
		trades24h: trade?.recent_24h_count,
		trades1h: trade?.recent_1h_count,
		trades5m: trade?.recent_5m_count,
		uniqueTraders24h: trade?.unique_traders_24h,
		openOrders: orders?.open_orders,
		totalOrders: orders?.total_orders,
		volumeSeries,
		lastHourVolume,
	};
}
